import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../../utils/apiConfig';

interface BusinessSetting {
    id: string;
    businessName: string;
    invoicePrefix: string;
    gstin: string | null;
    addressLine1: string | null;
    addressLine2: string | null;
    addressLine3: string | null;
    email: string | null;
    phoneNumber: string | null;
    invoiceCounter: number;
    isActive: boolean;
    _count?: {
        invoices: number;
    };
}

interface Props {
    token: string;
    onClose: () => void;
}

export default function BusinessSettingsModal({ token, onClose }: Props) {
    const [settings, setSettings] = useState<BusinessSetting[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingSetting, setEditingSetting] = useState<BusinessSetting | null>(null);
    const [formData, setFormData] = useState({
        businessName: '',
        invoicePrefix: '',
        gstin: '',
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        email: '',
        phoneNumber: '',
    });

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE_URL}/api/saas/invoices/business-settings`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) throw new Error('Failed to fetch settings');

            const data = await response.json();
            setSettings(data.data || []);
        } catch (error) {
            console.error('Error fetching settings:', error);
            toast.error('Failed to fetch business settings');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            const url = editingSetting
                ? `${API_BASE_URL}/api/saas/invoices/business-settings/${editingSetting.id}`
                : `${API_BASE_URL}/api/saas/invoices/business-settings`;

            const response = await fetch(url, {
                method: editingSetting ? 'PUT' : 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to save setting');
            }

            toast.success(editingSetting ? 'Setting updated successfully' : 'Setting created successfully');
            setShowForm(false);
            setEditingSetting(null);
            setFormData({
                businessName: '',
                invoicePrefix: '',
                gstin: '',
                addressLine1: '',
                addressLine2: '',
                addressLine3: '',
                email: '',
                phoneNumber: '',
            });
            fetchSettings();
        } catch (error: any) {
            console.error('Error saving setting:', error);
            toast.error(error.message || 'Failed to save setting');
        }
    };

    const handleEdit = (setting: BusinessSetting) => {
        setEditingSetting(setting);
        setFormData({
            businessName: setting.businessName,
            invoicePrefix: setting.invoicePrefix,
            gstin: setting.gstin || '',
            addressLine1: setting.addressLine1 || '',
            addressLine2: setting.addressLine2 || '',
            addressLine3: setting.addressLine3 || '',
            email: setting.email || '',
            phoneNumber: setting.phoneNumber || '',
        });
        setShowForm(true);
    };

    const handleCopy = (setting: BusinessSetting) => {
        setEditingSetting(null);
        setFormData({
            businessName: `${setting.businessName} (Copy)`,
            invoicePrefix: '',
            gstin: setting.gstin || '',
            addressLine1: setting.addressLine1 || '',
            addressLine2: setting.addressLine2 || '',
            addressLine3: setting.addressLine3 || '',
            email: setting.email || '',
            phoneNumber: setting.phoneNumber || '',
        });
        setShowForm(true);
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure you want to delete this business setting?')) return;

        try {
            const response = await fetch(`${API_BASE_URL}/api/saas/invoices/business-settings/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to delete setting');
            }

            toast.success('Setting deleted successfully');
            fetchSettings();
        } catch (error: any) {
            console.error('Error deleting setting:', error);
            toast.error(error.message || 'Failed to delete setting');
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-gray-900">Invoice Business Settings</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-2xl"
                    >
                        ×
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {showForm ? (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Business Name *
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.businessName}
                                        onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Invoice Prefix * {editingSetting && '(Cannot be changed)'}
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        disabled={!!editingSetting}
                                        value={formData.invoicePrefix}
                                        onChange={(e) => setFormData({ ...formData, invoicePrefix: e.target.value.toUpperCase() })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                                        placeholder="e.g., INV, QUOTE"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        GSTIN
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.gstin}
                                        onChange={(e) => setFormData({ ...formData, gstin: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        placeholder="22AAAAA0000A1Z5"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Phone Number
                                </label>
                                <input
                                    type="tel"
                                    value={formData.phoneNumber}
                                    onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Address Line 1
                                </label>
                                <input
                                    type="text"
                                    value={formData.addressLine1}
                                    onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Address Line 2
                                </label>
                                <input
                                    type="text"
                                    value={formData.addressLine2}
                                    onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Address Line 3
                                </label>
                                <input
                                    type="text"
                                    value={formData.addressLine3}
                                    onChange={(e) => setFormData({ ...formData, addressLine3: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>

                            <div className="flex gap-3 pt-4">
                                <button
                                    type="submit"
                                    className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                                >
                                    {editingSetting ? 'Update' : 'Create'} Setting
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowForm(false);
                                        setEditingSetting(null);
                                        setFormData({
                                            businessName: '',
                                            invoicePrefix: '',
                                            gstin: '',
                                            addressLine1: '',
                                            addressLine2: '',
                                            addressLine3: '',
                                            email: '',
                                            phoneNumber: '',
                                        });
                                    }}
                                    className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    ) : (
                        <>
                            <div className="mb-4">
                                <button
                                    onClick={() => setShowForm(true)}
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                                >
                                    + Add New Business
                                </button>
                            </div>

                            {loading ? (
                                <div className="text-center py-8">
                                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                                </div>
                            ) : settings.length === 0 ? (
                                <div className="text-center py-8 text-gray-600">
                                    No business settings found. Create one to start generating invoices.
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {settings.map((setting) => (
                                        <div
                                            key={setting.id}
                                            className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                                        >
                                            <div className="flex justify-between items-start">
                                                <div className="flex-1">
                                                    <h3 className="text-lg font-semibold text-gray-900">
                                                        {setting.businessName}
                                                    </h3>
                                                    <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                                                        <div>
                                                            <span className="text-gray-600">Prefix:</span>{' '}
                                                            <span className="font-medium">{setting.invoicePrefix}</span>
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-600">Invoices Generated:</span>{' '}
                                                            <span className="font-medium">{setting._count?.invoices || 0}</span>
                                                        </div>
                                                        {setting.gstin && (
                                                            <div>
                                                                <span className="text-gray-600">GSTIN:</span>{' '}
                                                                <span className="font-medium">{setting.gstin}</span>
                                                            </div>
                                                        )}
                                                        {setting.email && (
                                                            <div>
                                                                <span className="text-gray-600">Email:</span>{' '}
                                                                <span className="font-medium">{setting.email}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {(setting.addressLine1 || setting.addressLine2 || setting.addressLine3) && (
                                                        <div className="mt-2 text-sm text-gray-600">
                                                            <div className="font-medium text-gray-700">Address:</div>
                                                            {setting.addressLine1 && <div>{setting.addressLine1}</div>}
                                                            {setting.addressLine2 && <div>{setting.addressLine2}</div>}
                                                            {setting.addressLine3 && <div>{setting.addressLine3}</div>}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex gap-2 ml-4">
                                                    <button
                                                        onClick={() => handleCopy(setting)}
                                                        className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
                                                        title="Copy details into a new business"
                                                    >
                                                        Copy
                                                    </button>
                                                    <button
                                                        onClick={() => handleEdit(setting)}
                                                        className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800"
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(setting.id)}
                                                        className="px-3 py-1 text-sm text-red-600 hover:text-red-800"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
